import { Localized } from "@fluent/react"
import { Box, IconButton, SxProps, Theme, Tooltip } from "@mui/material"
import AddCommentIcon from '@mui/icons-material/AddComment'
import { createContext, MouseEvent, MouseEventHandler, ReactNode, useContext, useSyncExternalStore } from "react"
import { AppId } from "../../app/appTypes"
import { RaAnchor, RaAnchorKind, raContextType } from "./raComments"

/**
 * Which comment is open, and therefore which fragment of the FMEA row is
 * highlighted.
 *
 * `TextWithComments` keeps its `activeComment` in ordinary state, because its
 * panel and its text sit inside one component. Here the comment and the cell it
 * points at are in different branches of a table that can run to hundreds of
 * rows, so the selection has to be shared — and putting it in a context *value*
 * would re-render every consumer on every click, which in this file means every
 * function row and every lazily-mounted editor inside it. See the note on
 * TestSection in SimpleRiskAnalysis.tsx for what that costs.
 *
 * So the context carries a stable store instead and never itself changes.
 * `useSyncExternalStore` re-renders a subscriber only when the boolean it reads
 * actually flips: one cell turns off, one turns on, the rest do nothing.
 */
export interface RaSelected {
    commentId: AppId
    anchorKey: string
}

export interface RaSelectionStore {
    subscribe: (onChange: () => void) => () => void
    snapshot: () => RaSelected | null
    select: (next: RaSelected | null) => void
}

export const createRaSelectionStore = (): RaSelectionStore => {
    let current: RaSelected | null = null
    const listeners = new Set<() => void>()

    return {
        subscribe: onChange => {
            listeners.add(onChange)
            return () => { listeners.delete(onChange) }
        },
        snapshot: () => current,
        select: next => {
            if (current?.commentId === next?.commentId && current?.anchorKey === next?.anchorKey) return
            current = next
            listeners.forEach(l => l())
        },
    }
}

/** A store of its own, so a component rendered outside the provider is inert rather than broken. */
export const RaSelectionContext = createContext<RaSelectionStore>(createRaSelectionStore())

export const useRaSelection = (): RaSelectionStore => useContext(RaSelectionContext)

/**
 * Which fragment a new comment is being written about — the anchor key of the
 * tile whose add button was clicked, or null.
 *
 * The button sits on the tile and the composer in the comments column, which
 * are as far apart in the tree as a selected comment and its cell; hence the
 * same stable-store arrangement as the selection.
 */
export interface RaComposeStore {
    subscribe: (onChange: () => void) => () => void
    snapshot: () => string | null
    set: (key: string | null) => void
}

export const createRaComposeStore = (): RaComposeStore => {
    let current: string | null = null
    const listeners = new Set<() => void>()

    return {
        subscribe: onChange => {
            listeners.add(onChange)
            return () => { listeners.delete(onChange) }
        },
        snapshot: () => current,
        set: next => {
            if (current === next) return
            current = next
            listeners.forEach(l => l())
        },
    }
}

export const RaComposeContext = createContext<RaComposeStore>(createRaComposeStore())

export const useRaCompose = (): RaComposeStore => useContext(RaComposeContext)

/** The key being composed, if it is one of `keys` — so a comments cell re-renders only when its own anchors are involved. */
export const useComposingIn = (keys: string[]): string | null => {
    const store = useRaCompose()
    return useSyncExternalStore(store.subscribe, () => {
        const k = store.snapshot()
        return k !== null && keys.includes(k) ? k : null
    })
}

const useIsComposing = (key: string): boolean => {
    const store = useRaCompose()
    return useSyncExternalStore(store.subscribe, () => store.snapshot() === key)
}

/**
 * Whether the current user may start a comment. A context of its own because
 * the rule lives in SimpleRiskAnalysis.tsx, which this module must not import.
 */
export const RaCanCommentContext = createContext<boolean>(false)

/** How a fragment and the comments that point at it find each other. */
export const anchorKey = (a: RaAnchor): string => `${a.contextType}|${a.contextGuid}`
export const anchorKeyOf = (kind: RaAnchorKind, guid: string): string => `${raContextType[kind]}|${guid}`

export const useIsAnchorSelected = (key: string): boolean => {
    const store = useRaSelection()
    return useSyncExternalStore(store.subscribe, () => store.snapshot()?.anchorKey === key)
}

export const useIsCommentSelected = (commentId: AppId): boolean => {
    const store = useRaSelection()
    return useSyncExternalStore(store.subscribe, () => store.snapshot()?.commentId === commentId)
}

/**
 * `outline` rather than `border`: every table cell already draws its own right
 * and bottom edge, and swapping a 1px border for a 2px one would nudge the whole
 * row. An outline is painted outside the box model, so nothing moves.
 */
export const raSelectedCellSx: SxProps<Theme> = {
    outline: '2px solid var(--blue-400)',
    outlineOffset: '-2px',
    bgcolor: 'var(--blue-50)',
}

interface RaAnchoredCellProps {
    /** From `anchorKeyOf` — what a comment on this fragment points at. */
    anchorKey: string
    sx?: SxProps<Theme>
    className?: string
    /** The diagram views open their item menu from the fragment itself. */
    onContextMenu?: MouseEventHandler<HTMLDivElement>
    children: ReactNode
}

/**
 * A table cell that lights up while a comment about it is open.
 *
 * Its own component so the subscription sits on the smallest possible node:
 * `children` arrives as elements the parent already built, so a selection change
 * re-renders this Box and nothing beneath it — the editors inside are untouched.
 */
export const RaAnchoredCell = (props: RaAnchoredCellProps) => {
    const { anchorKey, sx = [], className, onContextMenu, children } = props
    const selected = useIsAnchorSelected(anchorKey)
    const composing = useIsComposing(anchorKey)
    const canComment = useContext(RaCanCommentContext)
    const lit = selected || composing

    return <Box
        className={className}
        onContextMenu={onContextMenu}
        data-ra-anchor={anchorKey}
        data-ra-selected={lit ? 'true' : undefined}
        // Array form: `sx` may legitimately be an array or a callback, and
        // spreading either of those silently yields an empty object.
        sx={[
            ...(Array.isArray(sx) ? sx : [sx]),
            canComment && addCommentRoomSx,
            lit && raSelectedCellSx,
        ]}
    >
        {children}
        {canComment && <AddCommentButton anchorKey={anchorKey} />}
    </Box>
}

// A band under the content for the button, so it never covers text. Below
// rather than beside: the table's tracks are narrow, and a band on the right
// would take its width from every line.
const addCommentRoomSx: SxProps<Theme> = {
    position: 'relative',
    pb: '26px',
}

/**
 * Starts a comment on the tile it sits in. Always shown, but faint: present on
 * every tile so it is found, quiet so a table of hundreds of tiles stays calm.
 */
const AddCommentButton = ({ anchorKey }: { anchorKey: string }) => {
    const compose = useRaCompose()
    const selection = useRaSelection()

    const handleClick = (e: MouseEvent) => {
        // The tile beneath may open its editor or its menu on a click.
        e.stopPropagation()
        selection.select(null)
        compose.set(anchorKey)
    }

    return <Tooltip placement='top-end' title={<Localized id='ra-comment-add'>Dodaj komentarz</Localized>}>
        <IconButton
            className='ra-add-comment-btn'
            size='small'
            onClick={handleClick}
            // A function tile is also a drag handle; the press must not start a drag.
            onPointerDown={e => e.stopPropagation()}
            sx={{
                position: 'absolute',
                bottom: '3px',
                right: '16px',
                p: .25,
                color: 'var(--gray-300)',
                '&:hover, &:focus-visible': { color: 'var(--gray-600)' },
            }}
        >
            <AddCommentIcon sx={{ width: '20px', height: '20px' }} />
        </IconButton>
    </Tooltip>
}
